import request from "@/http/http"
import type { StoreInfo } from "@/api"
// ==============================================
export interface PoiCooStatusDTO {
  // 合作方式（BusinessCooMode）
  cooMode: string
  // 合作状态（BusinessCooStatus）
  cooStatus: string
  // 操作人员名称
  username: string
  // 操作人员电话
  phone: string
  // 拓展部门
  orgs?: string
}

export type CompanyPoiCooStatusDTO = PoiCooStatusDTO

export type BusinessPoiCooStatusDTO = PoiCooStatusDTO

// 拜访记录
export interface VisitRecordVO {
  visitTime: string
  remark: string
  visitor: string
}

export interface CityRegion {
  id: number | string
  name: string
  level: number
  unlocked: boolean
  children?: CityRegion[]
}

interface CityRegionArgs {
  // 地区编码
  cityId: string
  // 沙盘ID
  sandTableId: string
}

// 获取城市区县街道
export async function getCityRegion(args: CityRegionArgs): Promise<CityRegion[]> {
  try {
    const result = await request.get<CityRegion[]>(`/sceneExpand/scene/areaRegion`, {
      params: args,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface BrandStoreArgs {
  brandId: string
  cityId: string
  query: string
}

export interface BrandStore {
  poiId: string
  poiName: string
  latitude: number
  longitude: number
  address: string
  // 此网点是否开通/解锁, false-未开通，true-已开通
  unlocked: boolean
}

// 筛选搜索品牌网点
export async function getBrandStore(params: BrandStoreArgs): Promise<BrandStore[]> {
  try {
    const result = await request.post<BrandStore[]>(`/business/poi/scene/list`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}

// 场景拓客-主页面-筛选条件
export interface MallScreeningConditionVO {
  // 城市品牌id
  cityId?: string
  brandId?: string

  // 筛选类型
  queryType?: string

  // 客流数据
  flowData?: {
    flowValue: string
    flowType: string
    flowFilterType: string
  }
  estateManagementCompanies?: string[]
  // 房价区间
  housePriceRange?: number[][]
  // 注册资本
  capitalRange?: number[][]
  // 注册时间
  capitalDateRange?: string[][]
  // 融资轮次
  financingRounds?: string[]
  // 联系方式
  cppRecommend?: string

  // queryType==CITY_RANGE时传参
  districtId?: string // 区县
  streetId?: string // 街道

  // queryType==STORE时传参
  latitude?: number // 中心点纬度
  longitude?: number // 中心点经度
  branchId?: string // 网点select的值(poiId)
  radius?: number // 半径
  radiusRange?: number[] // 网点半径范围
  isDiff?: boolean // 是否为差集
}

export interface RangeVO {
  key: string
  range?: number[]
  options: string[]
  showName: string
}

// 按组件拆分筛选条件的值
export interface SceneListParamsVO {
  // 主筛选
  QueryType: {
    queryType: string // 主筛选
    districtId?: string | undefined // 区县
    districtName?: string
    streetId: string | undefined // 街道
    streetName: string
    branchId: string // 网点select值(poiId)
    branchName: string
    latitude?: number // 纬度
    longitude?: number // 经度
    showName: string
    isStart?: boolean
  }
  // 物业
  Property: {
    options: any[]
  }
  // 房价区间
  HousePriceRange: RangeVO
  // 注册资本
  CapitalRange: RangeVO
  // 注册时间
  CapitalDateRange: RangeVO
  // 联系方式
  CppRecommend: RangeVO
  // 政府机构
  Government: RangeVO
  // 工商税务机构
  Taxation: RangeVO
  // 融资轮次
  FinancingRounds: RangeVO
  // 到访指数
  Flow: {
    flowType: string // 客流类型
    flowFilterType: string // 客流单位
    flowValue: string // 客流值
  }
}

export interface IndustryByLevelVO {
  id: string
  name: string
  children?: any
}

// 按级别获取行业标签
export async function getIndustryByLevel(): Promise<IndustryByLevelVO[]> {
  try {
    const result = await request.get<IndustryByLevelVO[]>(`/business/common/industry`)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface ScenePointProfileArgs {
  cityId: string // 城市id
  brandId: string // 品牌id
  moduleType: string // 模块名称（IndustryModuleType）
  sceneCustomerType: string // 场景拓客类型（SceneCustomerType）
  poiId: string // poiId
  latitude: number // 纬度
  longitude: number // 经度
  centralLatitude?: number | null // 中心点纬度
  centralLongitude?: number | null // 中心点经度
  businessType?: string | null // 商户类型
}

export interface BankExchangeInfo {
  dateTime: string
  exchangeInfo: string
  userName: string
}

export interface BusinessExchangeInfo {
  // cooStatusId
  cooStatusId: string
  // 合作状态
  cooStatus: boolean
  // 用户维护的商户名称
  poiNameForUser: string
  // 用户维护的商户地址
  addressForUser: string
  // 用户维护的商户状态
  storeStatusForUser: string
  // 用户维护的商户联系人姓名
  contactNameForUser: string
  // 用户维护的商户联系电话
  contactPhoneForUser: string
  // 维护人员工号
  jobNumberForUser: string
  // 维护人员名字
  userNameForUser: string
  // 银行商户id
  businessId: string
  // 维护网点
  storeInfoForUser: StoreInfo
  // 与商户沟通信息
  exchangeInfoForUser: BankExchangeInfo[]
  // 场景商业类型
  businessType: string
}

interface ScenePointProfile {
  name: string // 名字
  dataDate: string // 数据统计时间
  openingDate: string // 开业时间/成立日期
  address: string // 地址
  distance: number // 距离-米
  latitude: number // 纬度
  longitude: number // 经度
  lastMonthFlowCount: number // 上月客流（总客流）
  residentFlowCount: number // 上月客流（常驻客流）
  hourRatio: any // 上月月度平均分时指数
  age: any // 年龄分布
  consumption: any // 消费能力
  housePrice: any // 房产级别
  carAssets: any // 资产情况
  ecologyType: string // 生态类型（中文）
  ecoType: string // 生态信息类型（枚举，配合ecologyInfo使用）?????
  sceneCustomerType: string // 生态信息类型（SceneCustomerType ）
  ecologyInfo: any // 生态信息
  aoiPolygon: number[][] // aoi围栏
  storeName: string // 网点名称
  businessExchangeInfo: BusinessExchangeInfo // 商户合作场景用户维护的信息
}

// 获取指定地点的poi画像
export async function getScenePointProfile(
  params: ScenePointProfileArgs,
): Promise<ScenePointProfile> {
  try {
    const result = await request.post<ScenePointProfile>("/v1/industry/scenePointProfile", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface GovSceneArgs {
  areaCode: string // 城市id
  brandId: string // 品牌id
  moduleType: string // 模块名称（IndustryModuleType）
  sceneCustomerType: string // 场景拓客类型（SceneCustomerType）
  queryType: string // 筛选类型（SceneCustomerQueryType）
  hasPhone: boolean
  page: number // 页码
  limit: number // 每页数量

  govQueryTypes?: string[] // 政府机构
  commerceTaxQueryTypes?: string[] // 工商税务机构

  // queryType==CITY_RANGE时传参
  districtId?: string // 区县
  streetId?: string // 街道

  // queryType==STORE时传参
  poiId?: string
  lat?: number // 纬度
  lon?: number // 经度
  radius?: number // 半径
}
export interface GovSceneListVO {
  poiId: string
  name: string
  latitude: number // 纬度
  longitude: number // 经度
  address: string
  firstLevel: string // 一级分类
  secondLevel: string // 二级分类
  businessHours: string // 办公时间
  phone: string
  jobDescription: string // 职能
}

interface GovSceneListResult {
  list: GovSceneListVO[]
  total: number
}

// 获取政府场景拓客列表
export async function getGovSceneList(params: GovSceneArgs): Promise<GovSceneListResult> {
  try {
    const result = await request.post<GovSceneListResult>(`/sceneExpand/scene/government`, params)
    return result?.data
  } catch (error) {
    throw error
  }
}
